import React from 'react';
import clsx from 'clsx';
import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import type { LinkProps as NextLinkProps } from 'next/link';
import NextLink from 'next/link';
import type { LinkProps as MuiLinkProps } from '@mui/material/Link';
import MuiLink from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import { useConfig, useCookies } from '@packages/utilities';

// Add support for the sx prop for consistency with the other branches.
const Anchor = styled('a')({
  color: 'inherit',
  textDecoration: 'none',
});

interface NextLinkComposedProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>,
    Omit<NextLinkProps, 'href' | 'as' | 'onMouseEnter' | 'onClick' | 'onTouchStart'> {
  to: NextLinkProps['href'];
  linkAs?: NextLinkProps['as'];
  href?: NextLinkProps['href'];
}

export const NextLinkComposedWorkaround = React.forwardRef<
  HTMLAnchorElement,
  Omit<NextLinkComposedProps & { router: NextRouter }, 'href'>
>((props, ref) => {
  const { to, linkAs, replace, scroll, shallow, prefetch, locale, router, onClick, ...other } =
    props;

  return (
    <Anchor
      href={to.toString()}
      ref={ref}
      onMouseEnter={() => {
        if (prefetch) {
          router.prefetch(to.toString());
        }
      }}
      onClick={(event) => {
        onClick?.(event);

        if (event.defaultPrevented) return;

        event.stopPropagation();
        event.preventDefault();
        if (replace) {
          router.replace(to, linkAs, { shallow, locale, scroll });
        } else {
          router.push(to, linkAs, { shallow, locale, scroll });
        }
      }}
      {...other}
    />
  );
});

export const NextLinkComposed = React.forwardRef<HTMLAnchorElement, NextLinkComposedProps>(
  (props, ref) => {
    const { to, linkAs, href, replace, scroll, shallow, prefetch, locale, ...other } = props;

    return (
      <NextLink
        href={to}
        prefetch={false}
        as={linkAs}
        replace={replace}
        scroll={scroll}
        shallow={shallow}
        passHref
        legacyBehavior
        locale={locale}
      >
        <Anchor ref={ref} {...other} />
      </NextLink>
    );
  },
);

export type LinkProps = {
  activeClassName?: string;
  linkAs?: NextLinkProps['as']; // Useful when the as pro p is shallow by styled().
  noLinkStyle?: boolean;
  openInNewTab?: boolean;
} & Omit<NextLinkComposedProps, 'to' | 'linkAs' | 'href'> &
  Omit<MuiLinkProps, 'href'> &
  Pick<NextLinkProps, 'href' | 'as'>;

const regAbsolute = /^([a-z]+:\/\/|\/\/)/i;

/**
 * Helper hook to get pathname of href if internal link and hostname included. Otherwise get complete href
 * @param {NextLinkProps['href']} href provided href for image link
 */
const useClearedHref = (href: NextLinkProps['href']) => {
  const {
    host: { domain },
  } = useConfig();
  const tenantHost = `www.${domain}`;
  let linkHref = typeof href === 'string' ? href : href.pathname ?? '/';
  let external = false;

  // if href string, check  if absolute url
  if (typeof href === 'string') {
    if (regAbsolute.test(href)) {
      // if protocol-relative-url, add protocol
      const absoluteHref = href.startsWith('//') ? `https:${href}` : href;
      const { hostname: hostNameLink } = new URL(absoluteHref);
      const hostNameClient = typeof window !== 'undefined' ? window?.location?.hostname : undefined;

      // if host equals testing systems or tenant domain, it is an internal link, but set linkhref to pathname
      if (
        [tenantHost, hostNameClient, 'localhost'].some(
          (testHost) => testHost && hostNameLink.indexOf(testHost) > -1,
        )
      ) {
        // TODO: check if it is a requirement that query params are not in linkHref
        linkHref = new URL(absoluteHref).pathname;
      } else {
        external = true;
      }
    }
    // if href urlObject, che ck if host exists
  } else if (href.host) {
    // if host does not equal to tenant domain, it is an external link
    if (href.host.indexOf(tenantHost) === -1) {
      linkHref = href.href ?? '/';
      external = true;
    }
  }

  return { linkHref, external };
};

/**
 *  A styled version  of the Next.js Link component: https://nextjs.org/docs/api-reference/next/link
 * */
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {
    activeClassName = 'active',
    as: linkAs,
    className: classNameProps,
    href = '',
    noLinkStyle,
    openInNewTab,
    role, // Link don't have roles.
    ...other
  } = props;

  const router = useRouter();
  // NOTE: this currently strips out query params and hash for object urls, but keeps them for string urls
  const { linkHref, external } = useClearedHref(href);

  // if we are in an appview, we can not render the next link. we need to use an default anchor so the redirect is not done via javascript
  const { getCookies } = useCookies();
  const { appView } = getCookies();
  const isApp = appView === 'true';

  const className = clsx(classNameProps, {
    [activeClassName]: router.pathname === linkHref && activeClassName,
  });

  const target = openInNewTab ? '_blank' : '_self';

  if (external) {
    if (noLinkStyle) {
      return <Anchor className={className} href={linkHref} ref={ref} target={target} {...other} />;
    }

    return <MuiLink className={className} href={linkHref} ref={ref} target={target} {...other} />;
  }

  if (isApp) {
    if (noLinkStyle) {
      return (
        <Anchor className={className} href={href.toString()} ref={ref} target={target} {...other} />
      );
    }

    return (
      <MuiLink className={className} href={href.toString()} ref={ref} target={target} {...other} />
    );
  }

  if (noLinkStyle) {
    if (href.toString().split('?')[0].split('#')[0].endsWith('/')) {
      return (
        <NextLinkComposedWorkaround
          className={className}
          ref={ref}
          to={href}
          target={target}
          router={router}
          {...other}
        />
      );
    }
    return (
      <NextLinkComposed className={className} ref={ref} to={href} target={target} {...other} />
    );
  }

  return (
    <MuiLink
      data-testid="link"
      component={
        href.toString().split('?')[0].split('#')[0].endsWith('/')
          ? NextLinkComposedWorkaround
          : NextLinkComposed
      }
      linkAs={linkAs}
      className={className}
      ref={ref}
      to={href}
      target={target}
      prefetch={false}
      router={router}
      {...other}
    />
  );
});
